<template>
  <div
    class="card"
    :class="{
      combined: roomLevel,
      abs: absCard,
    }"
  >
    <router-link
      v-if="roomLevel ? hotelId : id"
      class="card-img"
      :to="{
        name: 'content',
        params: {
          id: roomLevel ? hotelId : id,
        },
      }"
    >
      <template v-if="mainImg">
        <img
          v-if="typeof mainImg === 'string'"
          :key="mainImg"
          v-lazy="mainImg || '/images/placeholder.jpg'"
          :alt="`${title} photo`"
        >
        <img
          v-if="mainImg.src && mainImg.src2x"
          :key="mainImg.src"
          :srcset="`${mainImg.src}, ${mainImg.src2x} 2x`"
          :alt="`${title} photo`"
        >
      </template>
      <img
        v-else
        v-lazy="'/images/placeholder.jpg'"
        :alt="`${title} photo`"
      >
      <div
        v-if="images && images.length"
        class="gallery"
      >
        <div
          v-for="({ imageUrl, description }, i) in images"
          :key="`${title}-${i + 1}-photo`"
          v-lazy-container="{ selector: 'img' }"
          class="gallery-item"
        >
          <img
            :data-src="imageUrl"
            :alt="description"
          >
        </div>
      </div>
    </router-link>
    <div
      v-else
      class="card-img"
    >
      <img
        :key="mainImg"
        v-lazy="mainImg || '/images/placeholder.jpg'"
        :alt="`${title} photo`"
      >
      <div
        v-if="images && images.length"
        class="gallery"
      >
        <div
          v-for="({ imageUrl, description }, i) in images"
          :key="`${title}-${i + 1}-photo`"
          v-lazy-container="{ selector: 'img' }"
          class="gallery-item"
        >
          <img
            :data-src="imageUrl"
            :alt="description"
          >
        </div>
      </div>
    </div>
    <div class="card-content">
      <div class="card-info">
        <div class="col-left">
          <div class="header">
            <router-link
              v-if="roomLevel ? hotelId : id"
              :to="{
                name: 'content',
                params: {
                  id: roomLevel ? hotelId : id,
                },
              }"
              class="title"
            >
              {{ title }}
              <stars
                v-if="stars"
                :stars="stars"
              />
            </router-link>
            <h2
              v-else
              class="title"
            >
              {{ title }}
              <stars
                v-if="stars"
                :stars="stars"
              />
            </h2>
          </div>
          <location v-if="location">
            {{ location }}
          </location>
          <div>
            <template v-if="roomLevel">
              Room only,
              <span
                :class="{
                  'free-cancellation': refundability === 'RF',
                }"
              >
                {{ refundabilityLabels[refundability] }}
              </span>
            </template>
            &nbsp;
            <a
              v-if="roomLevel"
              href="#"
              class="view-fees"
              @click.prevent
            >View Fees</a>
          </div>
          <div
            v-if="features && features.length"
            class="features-holder"
          >
            <div
              v-for="(feature, i) in features"
              :key="`${title}-feature-${i}`"
              class="features-item"
              :class="{ negative: feature.negative }"
            >
              {{ feature.text }}
            </div>
          </div>
        </div>
        <div class="col-right">
          <div
            v-if="taImage && taImage !== 'NULL'"
            class="tripadvisor"
          >
            <img
              :src="taImage"
              :alt="taRating"
              :title="taRating"
            >
          </div>
          <router-link
            v-if="(roomLevel ? hotelId : id) && price"
            :to="{
              name: 'content',
              params: {
                id: roomLevel ? hotelId : id,
              },
            }"
            class="price"
          >
            <div
              class="price-value"
              :class="{ 'price-purple': purplePrice }"
            >
              ${{ price }}
            </div>
            <div class="price-caption">
              Per night
            </div>
          </router-link>
          <div
            v-else
            class="price"
          >
            <div
              class="price-value"
              :class="{ 'price-purple': purplePrice }"
            >
              ${{ price }}
            </div>
            <div class="price-caption">
              Per night
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <div
          v-if="itemFacilities && itemFacilities.length"
          class="facilities-holder"
        >
          <amenity
            v-for="facility in itemFacilities"
            :key="`${id}-facility-${facility.id}`"
            :matched="facility.matched"
            :name="facility.label"
            :icon="require(`!svg-inline-loader?removeTags=true!@/assets/icons/facilities/${roomLevel ? 'room' : 'hotel'}/${kebabCase(facility.id)}.svg`)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, kebabCase } from 'lodash';
import Stars from './tiny/Stars';
import Location from './tiny/Location';
import Amenity from './tiny/Amenity';
import { refundabilityLabels } from '@/helpers';

export default {
  name: 'search-results-card',
  components: {
    Stars,
    Location,
    Amenity,
  },
  props: {
    selectedFacilities: {
      type: Array,
      default() {
        return [];
      },
    },
    id: {
      type: [String, Number],
      required: true,
    },
    hotelId: {
      type: [String, Number],
      default: null,
    },
    mainImg: {
      type: [String, Object],
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    stars: {
      type: [Number, String],
      default: null,
    },
    price: {
      type: [Number, String],
      default: null,
    },
    purplePrice: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: null,
    },
    location: {
      type: String,
      default: null,
    },
    refundability: {
      type: String,
      default: null,
    },
    features: {
      type: Array,
      default: null,
    },
    facilities: {
      type: Array,
      default: null,
    },
    taImage: {
      type: String,
      default: null,
    },
    taRating: {
      type: [Number, String],
      default: null,
    },
    roomLevel: {
      type: Boolean,
      default: false,
    },
    absCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      refundabilityLabels,
    };
  },
  computed: {
    itemFacilities() {
      if (this.selectedFacilities.length === 0) return this.facilities && this.facilities.slice(0, 6);

      const selectedFacilitiesLowercase = this.selectedFacilities.map(item => item.toLowerCase());
      let facilities = [];

      selectedFacilitiesLowercase.forEach(name => {
        const facility = this.facilities.find(({ label }) => label.toLowerCase() === name);

        if (facility) {
          facilities.push({
            ...facility,
            matched: true,
          });
        }
      });

      facilities = facilities.concat(this.facilities.filter(({ label }) => !selectedFacilitiesLowercase.includes(label.toLowerCase())));

      return facilities.slice(0, 6);
    },
  },
  methods: {
    kebabCase,
    debounce,
  },
};
</script>

<style scoped lang="scss">
.card {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 22px;
  border-radius: 4px;
  text-align: left;
  font-size: 13px;
  color: #6e7c91;
  background-color: #fff;
  box-shadow: 0 6px 25px 4px rgba(114, 132, 160, 0.18);

  @media (min-width: 1200px) {
    flex-direction: row;
  }

  &.combined {
    margin-bottom: 0;
    box-shadow: none;
    border-radius: 0;

    &:not(.abs) {
      padding: 16px 0;
      border-top: 1px solid #edf1f6;

      &:last-child {
        padding-bottom: 0;
      }
    }
  }
  &.abs {
    padding: 0 0 16px 0;
    border-bottom: 1px solid #edf1f6;
  }

  &-img {
    flex-shrink: 0;
    position: relative;
    width: 100%;
    max-width: 480px;
    height: 0;
    padding-bottom: min(60%, 290px);

    @media (min-width: 1200px) {
      max-width: 320px;
      padding-bottom: 192px;
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      background: #faebd7;
      text-align: center;
      color: #333;
    }

    .gallery {
      position: absolute;
      left: 0;
      bottom: 0;
      font-size: 0;
      line-height: 0;

      &-item {
        display: inline-block;
        border: 0 solid #fff;
        border-width: 2px 2px 0 0;
        width: 50px;
        height: 50px;
        position: relative;
      }
    }
  }

  &-content {
    flex-grow: 1;
    padding: 24px 0 10px;
    display: flex;
    flex-direction: column;

    @media (min-width: 1200px) {
      padding: 10px 0 10px 24px;
    }
  }

  &-info {
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .header {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 10px;

      @media (min-width: 1320px) {
        flex-direction: row;
      }
    }

    .title {
      color: #32325a;
      text-decoration: none;
      font-size: 17px;
      font-weight: bold;
      display: inline;
    }
    a.title {
      .no-touchevents &:hover {text-decoration: underline;}
      &:active {text-decoration: underline;}
    }
    .free-cancellation {
      color: #3bc069;
      font-weight: 700;
    }

    .view-fees {
      color: #6972de;
      text-decoration: underline;
      font-weight: 700;
      cursor: pointer;

      .no-touchevents &:hover {
        text-decoration: none;
      }
      &:active {
        text-decoration: none;
      }
    }

    .features {
      &-holder {
        flex-grow: 1;
        padding-top: 10px;
      }

      &-item {
        display: inline-block;
        padding: 5px 10px;
        margin: 0 10px 10px 0;
        font-size: 10px;
        font-weight: 700;
        color: #8fb6f5;
        border-radius: 2px;
        background-color: rgba(#8fb6f5, 0.2);

        &.negative {
          color: #ef5d5d;
          background-color: rgba(#ffb6b6, 0.2);
        }
      }
    }

    .col {
      &-left {
        margin-right: 16px;
      }

      &-right {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        @media screen and (max-width: 1600px) {
          display: block;
        }
      }
    }

    .tripadvisor {
      height: 15px;
      margin-top: 12px;
      @media screen and (max-width: 1600px) {
        display: block;
        margin-top: 0;
        & + * {margin-top: 12px;}
      }
      img {
        display: block;
        height: 100%;
        width: auto;
      }
    }

    .price {
      margin-left: 18px;
      text-align: center;
      text-decoration: none;
      color: currentColor;

      &-value {
        padding: 12px 14px;
        margin-bottom: 8px;
        border-radius: 5px;
        color: #fff;
        font-size: 15px;
        font-weight: 700;
        background-color: #56b282;
      }

      &-purple {
        background-color: #6d61fe;
      }

      &-caption {
        font-size: 11px;
        font-weight: 500;
        text-transform: uppercase;
      }
    }
  }

  .location {
    margin-bottom: 8px;
  }

  .stars {
    margin: 3px 0 3px 10px;
  }

  .facilities-holder {
    display: flex;
    flex-wrap: wrap;

    .item {
      margin: 8px 20px 0 0;
    }
  }
}
.fade-enter-active, .fade-leave-active {
  transition: opacity .15s;
}
.fade-enter, .fade-leave-to {
  opacity: 0.8;
}
</style>
